import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import {
  CurrencyInputProps,
  DateInputProps,
  FilterInputProps,
  TextInputProps,
} from "models/propTypes/wintFormPropsTypes";
import { ReceiptTodoPaymentSectionProps } from "models/propTypes/receiptPropTypes";
//#endregion
//#region components
import { Grid } from "@mui/material";
import {
  CurrencyInput,
  CurrencySelectionInput,
  DateInput,
  ExpandableSection,
  ExpandableSectionSummary,
  FilterInput,
  TextInput,
  VerticalFormField,
} from "components/common/wintForms";
import usePaymentMethods from "../../paymentMethods/forms/usePaymentMethods";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import { OTHER_PAYMENT_METHOD_ID } from "utils/constants/receiptConstants";

const ReceiptTodoPaymentSection: React.FC<ReceiptTodoPaymentSectionProps> = ({
  connect,
  receipt,
  expandableStepProps,
}) => {
  const { receiptPaymentMethods, addPaymentAction } = usePaymentMethods(receipt);

  const currencyConnect = connect("currency");
  const paymentMethodIdConnect = connect("paymentMethodId");
  const amountConnect = connect("amount");
  const purchaseDateConnect = connect("purchaseDate");
  const paymentMethodNameConnect = connect("paymentMethod");
  const paymentMethodTypeConnect = connect("paymentMethodType");
  const otherPaymentMethodExplanationConnect = connect("otherPaymentMethodExplanation");
  const isOtherPaymentMethodConnect = connect("isOtherPaymentMethod");

  const [showOtherPaymentMethodExplanation, setShowOtherPaymentMethodExplanation] = React.useState(
    receipt.isOtherPaymentMethod
  );
  const isDisabledPaymentMethodType = (paymentMethodType?: WintSuperkollAPIModel.ReceiptPaymentMethodType) => {
    return (
      paymentMethodType === WintSuperkollAPIModel.ReceiptPaymentMethodType.WintCard ||
      paymentMethodType === WintSuperkollAPIModel.ReceiptPaymentMethodType.MissingDocument
    );
  };

  const onPaymentMethodChange = (value: number) => {
    paymentMethodIdConnect.setValue(value);
    const paymentMethod = receiptPaymentMethods.find((p) => p.id === value);
    if (paymentMethod) {
      paymentMethodNameConnect.setValue(paymentMethod.value);
      paymentMethodTypeConnect.setValue(paymentMethod.data?.type);
    }
    const isOther = paymentMethod?.id === OTHER_PAYMENT_METHOD_ID;

    setShowOtherPaymentMethodExplanation(isOther);
    isOtherPaymentMethodConnect.setValue(isOther);
  };

  const isNonEditablePaymentMethodType = isDisabledPaymentMethodType(paymentMethodTypeConnect.value);

  return (
    <ExpandableSection
      summary={
        <ExpandableSectionSummary
          title={receipt.isOtherPaymentMethod 
            ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.PaymentMethod)
            : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Information)
          }
        />
      }
      initiallyOpen
      hasErrors={
        paymentMethodIdConnect.hasErrors ||
        currencyConnect.hasErrors ||
        amountConnect.hasErrors ||
        purchaseDateConnect.hasErrors
      }
      {...expandableStepProps}
    >
      {!receipt.isOtherPaymentMethod && (
        <>
          <VerticalFormField<DateInputProps>
            Component={DateInput}
            {...purchaseDateConnect}
            label={TranslationKeys.Receipt.PurchaseDate}
            disabled={isNonEditablePaymentMethodType}
          />

          <Grid container direction="row" columnGap={1} wrap="nowrap">
            <Grid item xs={6}>
              <VerticalFormField<CurrencyInputProps>
                Component={CurrencyInput}
                {...amountConnect}
                label={TranslationKeys.Common.AmountInclVAT}
                currency={currencyConnect.value}
                disabled={isNonEditablePaymentMethodType}
              />
            </Grid>
            <Grid item xs={6}>
              <VerticalFormField
                Component={CurrencySelectionInput}
                {...currencyConnect}
                label={TranslationKeys.Common.Currency}
                disabled={isNonEditablePaymentMethodType}
              />
            </Grid>
          </Grid>
        </>
      )}
      {isNonEditablePaymentMethodType ? (
        <VerticalFormField<TextInputProps>
          Component={TextInput}
          {...paymentMethodNameConnect}
          label={TranslationKeys.Receipt.PaymentMethod}
          disabled
        />
      ) : (
        <VerticalFormField<FilterInputProps>
          Component={FilterInput}
          {...paymentMethodIdConnect}
          setValue={onPaymentMethodChange}
          label={TranslationKeys.Receipt.PaymentMethod}
          items={receiptPaymentMethods}
          actions={addPaymentAction}
        />
      )}
      {showOtherPaymentMethodExplanation && (
        <VerticalFormField<TextInputProps>
          Component={TextInput}
          {...otherPaymentMethodExplanationConnect}
          label={TranslationKeys.Receipt.OtherPaymentExplanation}
          maxLength={300}
        />
      )}
    </ExpandableSection>
  );
};

export default ReceiptTodoPaymentSection;
